/**
 * A living view of the signed-in account's apps, for the model's context.
 *
 * Holds the last list fetched from GET /m5t/v5/sseEtherSubscriberApp/mine and
 * renders a compact summary into the chat system prompt, so the model can answer
 * "how many of my apps are active?" from fact rather than invention, and can
 * refer to an app the user names.
 *
 * WHAT IS DELIBERATELY NOT HERE
 *
 * `guid`. A model copying a 36-character UUID gets it subtly wrong often enough
 * to matter, and a wrong guid is a confusing 404 rather than an obvious failure.
 * The model refers to an app by NAME; resolveByName() turns that into a record
 * using the same list that produced the context. Identity resolution stays in
 * JS, where it cannot hallucinate.
 *
 * Audit trails (`created`, `modified`), `app_keys` and `sprite_imagery` are also
 * omitted. `/mine` already withholds keys and imagery; the audit columns are
 * fetched because the card renders "Updated 3 days ago" from them, but they are
 * noise to the model and every token here is re-prefilled on every turn.
 *
 * NOT THE ROUTING PROMPT. This feeds chat() only. routingPrompt() is a pure
 * function of the registry - putting conversation-dependent data in front of the
 * router is exactly what made routing look flaky, twice.
 */

import { appTitle, appHost } from '../templates/etherApps.js';

/**
 * Beyond this the summary is elided. A 200-app account would otherwise spend
 * thousands of tokens per turn describing itself, and the tail is not what
 * anyone asks about.
 */
const MAX_LISTED = 40;

/** @type {Array|null} null means "never loaded" - distinct from "loaded, empty". */
let apps = null;

/** Replace the cache. Called after any fetch or mutation that changes the list. */
export function setApps(list) {
  apps = Array.isArray(list) ? list : [];
}

export function getApps() {
  return apps ?? [];
}

export function isLoaded() {
  return apps !== null;
}

export function clearApps() {
  apps = null;
}

/** Case- and space-insensitive, so "hvac wiki" matches "HVAC WIKI". */
const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Resolve a user-supplied name to one app.
 *
 * Exact match before substring, so an app literally called "Scott" wins over
 * "Scott's Archive". Ambiguity is reported rather than guessed - picking one of
 * two plausible matches is how a user ends up acting on the wrong record.
 *
 * @returns {{app: object}|{ambiguous: object[]}|null}
 */
export function resolveByName(name) {
  const wanted = norm(name);
  if (!wanted || !apps?.length) return null;

  const withNames = apps.map((a) => ({ app: a, name: norm(appTitle(a)) }));

  const exact = withNames.filter((x) => x.name === wanted);
  if (exact.length === 1) return { app: exact[0].app };
  if (exact.length > 1) return { ambiguous: exact.map((x) => x.app) };

  const partial = withNames.filter((x) => x.name.includes(wanted) || wanted.includes(x.name));
  if (partial.length === 1) return { app: partial[0].app };
  if (partial.length > 1) return { ambiguous: partial.map((x) => x.app) };

  return null;
}

/**
 * The system-prompt contribution: what this user has, in about 20 tokens per app.
 *
 * Returns null when nothing has been loaded yet, so SystemPromptProvider drops
 * the section entirely rather than asserting an empty account - "you have no
 * apps" to someone who has four is worse than saying nothing.
 */
export function appsContext() {
  if (!isLoaded()) return null;

  const list = getApps();
  if (!list.length) return 'The user has no apps yet.';

  const shown = list.slice(0, MAX_LISTED);

  const lines = shown.map((a) => {
    const status = a?.status && a.status !== 'ENABLED' ? ` (${a.status})` : '';
    const host = appHost(a);
    return `- ${appTitle(a)}${host ? ` — ${host}` : ''}${status}`;
  });

  const enabled = list.filter((a) => a?.status === 'ENABLED').length;
  const header = `The user has ${list.length} app(s), ${enabled} enabled:`;
  const elided = list.length > shown.length ? [`- ...and ${list.length - shown.length} more`] : [];

  return [
    header,
    ...lines,
    ...elided,
    'Refer to an app by name. You cannot see an app\'s keys, contents or traffic.'
  ].join('\n');
}

export default { setApps, getApps, isLoaded, clearApps, resolveByName, appsContext };
